import { currencyFormatterHelpers } from '@/utils/currencyFormatter';

const { formatCurrencyNumber: formatNumberWithCommas } =
  currencyFormatterHelpers;

/**
 * Checks if an offer salary is above the requisition's salary range
 * @param offerSalary - The offer salary (can be string or number)
 * @param maxSalary - The maximum salary from the requisition
 * @returns true if the offer salary exceeds the max salary
 *
 * @example
 * isSalaryAboveRange(100000, 80000) // Returns: true
 * isSalaryAboveRange("75000.00", 80000) // Returns: false
 */
export const isSalaryAboveRange = (
  offerSalary?: string | number | null,
  maxSalary?: number | null
): boolean => {
  if (!offerSalary || maxSalary === undefined || maxSalary === null) {
    return false;
  }

  const offerSalaryNum = parseFloat(offerSalary.toString());
  return !isNaN(offerSalaryNum) && offerSalaryNum > maxSalary;
};

/**
 * Formats a salary range for display
 * @param minSalary - The minimum salary
 * @param maxSalary - The maximum salary
 * @param currency - The currency code
 * @returns A formatted salary range string
 *
 * @example
 * formatSalaryRange(50000, 80000, "USD") // Returns: "$50,000 - $80,000 USD"
 */
export const formatSalaryRange = (
  minSalary: number,
  maxSalary: number,
  currency: string
): string => {
  const formattedMin = formatNumberWithCommas(minSalary, currency, true);
  const formattedMax = formatNumberWithCommas(maxSalary, currency, true);
  return `$${formattedMin} - $${formattedMax} ${currency}`;
};

/**
 * Formats salary for display in offer details
 * @param salary - The salary amount (can be string or number)
 * @param currency - The currency object with currency code
 * @returns A formatted string like "50000 (USD)" or "-" if no salary
 *
 * @example
 * formatOfferSalary("50000.00", { id: 1, currency: "USD" })
 * // Returns: "50000 (USD)"
 *
 * formatOfferSalary(50000, { id: 1, currency_code: "USD" })
 * // Returns: "50000 (USD)"
 */
export const formatOfferSalary = (
  salary?: string | number | null,
  currency?: {
    id?: number;
    currency?: string;
    currency_code?: string;
  }
): string => {
  if (!salary) return '-';

  const salaryValue =
    typeof salary === 'string'
      ? salary.split('.')?.[0]
      : Math.floor(salary).toString();

  const currencyCode = currency?.currency || currency?.currency_code;
  const currencyDisplay =
    currency?.id && currencyCode ? ` (${currencyCode})` : '';

  const formattedSalary = formatNumberWithCommas(
    salaryValue,
    currencyCode,
    true
  );

  return `${formattedSalary}${currencyDisplay}`;
};

/**
 * Constructs a formatted equity label from equity award details
 * @param ats_equity_award - The equity award object containing vesting and amount details
 * @returns A formatted string describing the equity award or undefined if no award
 *
 * @example
 * constructEquityLabel({
 *   amount: "10000",
 *   currency: { id: 1, currency: "USD" },
 *   cliff_of_vesting: "1",
 *   unit_of_cliff: "year",
 *   cycles_after_cliff: "12",
 *   unit_of_cycle_after_cliff: "month",
 *   period_of_vesting: "4",
 *   unit_of_vesting_period: "year"
 * })
 * // Returns: "10000 USD, 1 year cliff, 12 months post-cliff cycle, 4 years vesting period"
 */
export const constructEquityLabel = (ats_equity_award?: {
  amount?: string;
  cliff_of_vesting?: string;
  unit_of_cliff?: string;
  period_of_vesting?: string;
  unit_of_vesting_period?: string;
  cycles_after_cliff?: string;
  currency?: any;
  unit_of_cycle_after_cliff?: string;
}): string | undefined => {
  if (!ats_equity_award) return undefined;

  const {
    amount,
    currency,
    cliff_of_vesting,
    unit_of_cliff,
    period_of_vesting,
    unit_of_vesting_period,
    cycles_after_cliff,
    unit_of_cycle_after_cliff,
  } = ats_equity_award;

  const parts: string[] = [];

  // Format the amount/units if available
  if (amount) {
    const value = amount
      ? `${amount?.split('.')?.[0]} ${
          currency?.id ? currency?.currency : 'Units'
        }`
      : '';
    parts.push(value);
  }

  // Cliff vesting period
  if (cliff_of_vesting) {
    const unitCliff =
      unit_of_cliff && parseInt(cliff_of_vesting) > 1
        ? `${unit_of_cliff}s`
        : unit_of_cliff;
    parts.push(`${cliff_of_vesting} ${unitCliff || ''} cliff`);
  }

  // Post-cliff cycles
  if (cycles_after_cliff) {
    const unitCycle =
      unit_of_cycle_after_cliff && parseInt(cycles_after_cliff) > 1
        ? `${unit_of_cycle_after_cliff}s`
        : unit_of_cycle_after_cliff;
    parts.push(`${cycles_after_cliff} ${unitCycle || ''} post-cliff cycle`);
  }

  // Vesting period
  if (period_of_vesting) {
    const unitVesting =
      unit_of_vesting_period && parseInt(period_of_vesting) > 1
        ? `${unit_of_vesting_period}s`
        : unit_of_vesting_period;
    parts.push(`${period_of_vesting} ${unitVesting || ''} vesting period`);
  }

  return parts.length ? parts.join(', ') : undefined;
};
